import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, GradButton } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { TIP_AD } from "@/components/finans/FinansSekmeler";
import { IbanInput, BankaInput, SubeInput } from "@/components/BankaAlanlari";
import api, { errMsg } from "@/lib/api";

const PB = ["TRY", "USD", "EUR", "GBP"].map((p) => ({ value: p, label: p }));
const bugun = () => new Date().toISOString().slice(0, 10);
const BOS = { ad: "", banka: "", sube: "", iban: "", hesap_no: "", para_birimi: "TRY", acilis_bakiye: 0, acilis_tarihi: "", etiketler: "", aciklama: "", aktif: true,
  kart_no: "", kart_limiti: 0, kesim_gunu: 9, odeme_gunu: 19, min_odeme_orani: 40 };

const BankaBilgi = ({ f, set }) => {
  const [mod, setMod] = useState(f.hesap_no && !f.iban ? "no" : "iban");
  return (
    <div className="space-y-3">
      <div className="flex gap-4 text-sm">{[["iban", "IBAN"], ["no", "Hesap No"]].map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={mod === k} onChange={() => setMod(k)} data-testid={`hesap-mod-${k}`} />{l}</label>
      ))}</div>
      {mod === "iban" ? <IbanInput testid="hesap-iban" value={f.iban} onChange={(v) => set("iban", v)} onBanka={(b) => !f.banka && set("banka", b)} />
        : <Input data-testid="hesap-no" value={f.hesap_no} onChange={(e) => set("hesap_no", e.target.value)} className="bg-white/5" />}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Banka"><BankaInput testid="hesap-banka" value={f.banka} onChange={(v) => set("banka", v)} /></Field>
        <Field label="Şube"><SubeInput testid="hesap-sube" banka={f.banka} value={f.sube} onChange={(v) => set("sube", v)} /></Field>
      </div>
    </div>
  );
};

const KartBilgi = ({ f, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Kart numarası" className="col-span-2"><Input data-testid="hesap-kart-no" value={f.kart_no} onChange={(e) => set("kart_no", e.target.value)} className="bg-white/5 font-mono" /></Field>
    <Field label="Kart limiti"><Input data-testid="hesap-limit" type="number" value={f.kart_limiti} onChange={(e) => set("kart_limiti", +e.target.value)} className="bg-white/5" /></Field>
    <Field label="Min. ödeme oranı (%)"><Input data-testid="hesap-min-odeme" type="number" value={f.min_odeme_orani} onChange={(e) => set("min_odeme_orani", +e.target.value)} className="bg-white/5" /></Field>
    <Field label="Hesap kesim günü"><Input data-testid="hesap-kesim" type="number" min={1} max={31} value={f.kesim_gunu} onChange={(e) => set("kesim_gunu", +e.target.value)} className="bg-white/5" /></Field>
    <Field label="Ekstre ödeme günü"><Input data-testid="hesap-odeme-gunu" type="number" min={1} max={31} value={f.odeme_gunu} onChange={(e) => set("odeme_gunu", +e.target.value)} className="bg-white/5" /></Field>
  </div>
);

export const HesapForm = ({ tip, hesap, onClose, onSaved }) => {
  const [f, setF] = useState({ ...BOS, acilis_tarihi: bugun(), ...(hesap || {}), tip: hesap?.tip || tip });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const kaydet = async () => {
    setBusy(true);
    try {
      const { data } = hesap ? await api.put(`/hesaplar/${hesap.id}`, f) : await api.post("/hesaplar", f);
      toast.success("Kaydedildi"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="hesap-form">
        <DialogHeader><DialogTitle>{hesap ? "Düzenle" : "Yeni"} {TIP_AD[f.tip]}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="text-sm font-semibold">Genel Bilgiler</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ad *"><Input data-testid="hesap-ad" value={f.ad} onChange={(e) => set("ad", e.target.value)} className="bg-white/5" /></Field>
              <Field label="Etiketler"><Input data-testid="hesap-etiket" value={f.etiketler} onChange={(e) => set("etiketler", e.target.value)} className="bg-white/5" /></Field>
              <Field label="Açılış tarihi *"><Input data-testid="hesap-acilis-tarih" type="date" value={f.acilis_tarihi} onChange={(e) => set("acilis_tarihi", e.target.value)} className="bg-white/5" /></Field>
              <Field label="Başlangıç bakiyesi"><Input data-testid="hesap-acilis" type="number" value={f.acilis_bakiye} onChange={(e) => set("acilis_bakiye", +e.target.value)} className="bg-white/5" /></Field>
              <Field label="Para birimi *"><SelectBox testid="hesap-pb" value={f.para_birimi} onChange={(v) => set("para_birimi", v)} options={PB} /></Field>
            </div>
            <Field label="Açıklama"><Textarea data-testid="hesap-aciklama" value={f.aciklama} onChange={(e) => set("aciklama", e.target.value)} className="bg-white/5" /></Field>
            {f.tip === "kredi_karti" && <p className="text-[11px] text-muted-foreground">Kartın açılıştaki borcunu eksi (-) olarak girin.</p>}
          </div>
          {f.tip !== "kasa" && <div className="space-y-3">
            <div className="text-sm font-semibold">{f.tip === "kredi_karti" ? "Kredi Kartı Bilgileri" : "Banka Bilgileri"}</div>
            {f.tip === "kredi_karti" ? <KartBilgi f={f} set={set} /> : <BankaBilgi f={f} set={set} />}
          </div>}
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 h-10 rounded-full text-sm bg-white/5 hover:bg-white/10">İptal</button>
          <GradButton data-testid="hesap-kaydet" onClick={kaydet} disabled={busy || !f.ad.trim()}>Kaydet</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};
